import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { getConsent, setConsent } from "@/lib/analytics";

export const Route = createFileRoute("/politica-de-privacidade")({
  head: () => ({
    meta: [
      { title: "Política de Privacidade — Contato Internet" },
      {
        name: "description",
        content:
          "Política de Privacidade e Política de Cookies da Contato Internet: quais dados coletamos, como usamos, compartilhamos e seus direitos conforme a LGPD.",
      },
    ],
  }),
  component: PrivacyPage,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="text-base font-bold text-brand-deep sm:text-lg">{title}</h2>
      <div className="mt-2 space-y-3">{children}</div>
    </section>
  );
}

function PrivacyPage() {
  const [consent, setConsentState] = useState(getConsent());

  const change = (state: "granted" | "denied") => {
    setConsent(state);
    setConsentState(state);
  };

  return (
    <div className="mx-auto max-w-3xl px-5 py-12 sm:py-16">
      <h1 className="text-2xl font-extrabold uppercase tracking-tight text-primary sm:text-3xl">
        Política de Privacidade
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">Última atualização: setembro de 2026</p>

      {/* Preferências de cookies — controle prático, além do texto legal abaixo */}
      <div className="mt-8 rounded-xl border border-border bg-card p-4">
        <p className="text-sm font-semibold text-brand-deep">Suas preferências de cookies</p>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-sm text-muted-foreground">
            Estado atual:{" "}
            <strong className="text-foreground">
              {consent === "granted"
                ? "cookies de medição aceitos"
                : consent === "denied"
                  ? "cookies de medição recusados"
                  : "sem escolha registrada"}
            </strong>
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => change("denied")}
              className="rounded-lg border border-input px-3 py-1.5 text-sm font-semibold transition hover:bg-accent"
            >
              Recusar
            </button>
            <button
              type="button"
              onClick={() => change("granted")}
              className="rounded-lg bg-primary px-3 py-1.5 text-sm font-extrabold uppercase tracking-wide text-primary-foreground transition hover:brightness-110"
            >
              Aceitar
            </button>
          </div>
        </div>
      </div>

      <div className="mt-10 space-y-8 text-sm leading-relaxed text-foreground">
        <p>
          A Política de Privacidade da Contato Internet Eireli abrange suas controladoras e
          operadoras, direta ou indiretamente, e foi criada para mostrar o nosso compromisso em
          tratar os seus dados pessoais com segurança, privacidade e transparência.
        </p>
        <p>
          Ela descreve os dados pessoais que coletamos, assim como eles são usados, armazenados e
          compartilhados, e os seus direitos em relação a esses dados, no que se refere a serviços
          de conectividade, como banda larga e Wi-Fi.
        </p>

        <Section title="Introdução">
          <p>
            Quando você contrata os serviços da Contato Internet, você nos fornece os seus dados
            pessoais para que possamos viabilizar a contratação de nossos produtos e serviços e
            também para lhe entregar a melhor experiência possível. Para nós, privacidade,
            segurança e transparência são valores fundamentais e sempre adotaremos as melhores
            práticas para garantir a confidencialidade e integridade dos seus dados pessoais.
          </p>
          <p>
            Ao aceitar os termos desta Política de Privacidade, você está ciente de que a
            controladora dos seus dados pessoais, ou seja, a empresa responsável por tomar as
            decisões sobre o tratamento dos seus dados pessoais, será a Contato Internet Eireli,
            inscrita no CNPJ sob nº 07.562.175/0001-31, com sede na Rua Antônio Bertoncini, nº
            515, bairro Cidade Alta, Araranguá (SC), CEP 88901-022.
          </p>
          <p>
            Após a leitura desta Política de Privacidade, se você tiver dúvidas, reclamações,
            quiser exercer seus direitos relacionados aos seus dados pessoais ou se comunicar com
            a Contato Internet sobre esse assunto, você pode entrar em contato conosco pelos
            nossos canais de atendimento ou contatar o nosso encarregado (DPO) pelo e-mail{" "}
            <a
              href="mailto:contato@contatointernet.com.br"
              className="font-semibold text-primary hover:underline"
            >
              contato@contatointernet.com.br
            </a>
            .
          </p>
        </Section>

        <Section title="1. Aplicação">
          <p>
            Cada vez que você ler um termo iniciado em letra maiúscula nesta Política, significa
            que ele é um termo com significado definido especificamente.
          </p>
          <p>
            Toda vez que houver menção aos termos "Contato", "nós" ou "nossos", estamos nos
            referindo à empresa Contato Internet Eireli; da mesma forma, toda vez que houver
            menção aos termos "você", "seu", "sua", estamos nos referindo a Você.
          </p>
          <p>
            Esta Política de Privacidade é aplicável a todos os clientes da Contato Internet. Nós
            coletamos os seus dados pessoais sempre que você:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Contratar ou usar qualquer um dos nossos produtos ou serviços, seja fazendo login em
              nossa rede aberta, disponível em empresas como o banco Sicoob, seja contratando
              qualquer um de nossos produtos ou serviços de internet;
            </li>
            <li>
              Navegar no website da Contato —{" "}
              <a href="https://www.contato.net/" className="text-primary hover:underline">
                www.contato.net
              </a>
              ;
            </li>
            <li>
              Navegar no blog da Contato —{" "}
              <a href="/blog" className="text-primary hover:underline">
                Blog Contato
              </a>
              ;
            </li>
            <li>Entrar em contato conosco pelos nossos canais de atendimento;</li>
            <li>Participar de pesquisas ou promoções realizadas pela Contato Internet.</li>
          </ul>
          <p>Esta Política de Privacidade se aplica especificamente aos:</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              <strong>Clientes da Contato:</strong> pessoas naturais que efetivamente contratem,
              utilizem ou acessem um ou mais serviços ou produtos da Contato (seja para você mesmo
              ou para sua pessoa jurídica);
            </li>
            <li>
              <strong>Prospectos da Contato:</strong> pessoas naturais prospectadas pela Contato
              ou que já tenham solicitado a contratação de um dos serviços ou produtos, mas que
              não tenham se tornado um cliente da Contato por qualquer motivo.
            </li>
          </ul>
          <p>
            Esta Política de Privacidade também é aplicável a outras formas de coleta de dados
            pessoais pela Contato, que permitem a prestação ou o aprimoramento dos nossos
            serviços. Por exemplo, podemos coletar informações por meio de parceiros ou relativas
            às nossas tecnologias. Todas as formas de coleta e os usos dos seus dados pessoais
            estão descritos nesta Política de Privacidade.
          </p>
          <p>
            As práticas descritas nesta Política de Privacidade só se aplicam ao tratamento dos
            seus dados pessoais no Brasil e estão sujeitas às leis locais aplicáveis, com destaque
            para a Lei nº 13.709/2018 (Lei Geral de Proteção de Dados Pessoais, ou "LGPD") a
            partir de sua entrada em vigor.
          </p>
        </Section>

        <Section title="2. Quais dados pessoais são coletados pela Contato">
          <p>
            Ao solicitar, contratar ou usar os produtos ou serviços da Contato, você nos fornece,
            e nós coletamos, alguns dados pessoais relacionados a você. Desde o momento em que
            você interage com a Contato, nós coletamos os seus dados pessoais. Em alguns casos,
            você fornece diretamente os seus dados pessoais, mas também podemos coletar seus dados
            de maneira automática.
          </p>
          <p>
            Além disso, nós também recebemos alguns dados pessoais enviados por parceiros que
            contratamos para finalidades específicas (como para reforçar nosso controle contra
            fraudes), para que possamos cumprir com obrigações legais ou com a regulamentação
            aplicável e outras que serão detalhadas mais adiante.
          </p>
          <p>
            Ao aceitar os termos desta Política de Privacidade, você concorda expressamente em
            fornecer apenas dados pessoais verdadeiros, atuais e precisos e em não alterar a sua
            identidade ou seus dados pessoais de qualquer forma no acesso e na utilização dos
            nossos produtos ou serviços. Você será o único responsável pelas informações falsas,
            desatualizadas ou imprecisas que fornecer diretamente para a Contato.
          </p>
          <p>Esses são os seus dados pessoais tratados pela Contato, divididos por categorias:</p>

          <p className="font-semibold text-brand-deep">Dados pessoais informados pelo titular</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Dados cadastrais, tais como: nome, documentos de identificação, nacionalidade,
              endereço, data de nascimento, filiação, gênero, entre outros;
            </li>
            <li>Dados de contato, como telefone e e-mail;</li>
            <li>Empresa em que trabalha;</li>
            <li>
              Dados biométricos, tais como a fotografia do seu documento de identificação e do seu
              rosto;
            </li>
            <li>
              Gravação da voz, durante chamadas para o atendimento da Contato, para que possamos
              monitorar a qualidade do nosso atendimento;
            </li>
            <li>
              Sua imagem, nas lojas (para auxiliar na autenticação e evitar que outras pessoas se
              passem por você) e na contratação digital, quando você envia sua foto segurando seu
              documento pelo WhatsApp para nosso canal de atendimento Huggy, para confirmar sua
              identidade.
            </li>
          </ul>

          <p className="font-semibold text-brand-deep">Dados pessoais que coletamos de terceiros</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Dados cadastrais, tais como: nome, filiação, data de nascimento, CPF, número de
              telefone, endereço, entre outros;
            </li>
            <li>
              Dados sobre restrições financeiras, tais como: negativações, valores devidos, datas
              de vencimento, quantidades de consultas, entre outros;
            </li>
            <li>Informações sobre histórico de crédito;</li>
            <li>Score gerado por bureaus de crédito;</li>
            <li>Informações de dívidas a vencer ou vencidas, coobrigações e garantias;</li>
            <li>
              Se você faz parte de alguma lista de Pessoa Politicamente Exposta (PPE) ou lista de
              restrição (como OFAC, CSNU e outras listas internacionais);
            </li>
            <li>
              Informações constantes da base de dados do Sistema de Informações de Crédito (SCR),
              mediante consentimento;
            </li>
            <li>
              Dados bancários ou de cartões de crédito e débito, inclusive por meio de parceiros
              com as certificações de segurança aplicáveis. Usamos esses dados somente para
              efetuar a cobrança pelos serviços contratados através da Contato.
            </li>
          </ul>

          <p className="font-semibold text-brand-deep">Dados de navegação e do dispositivo</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Informações sobre navegadores e dispositivos, incluindo relatórios de erros,
              atividade do sistema, data, hora e URL (caso o usuário consinta, no acesso remoto ou
              por meio de suporte técnico presencial): usadas conforme necessidades regulatórias e
              para monitorar a qualidade de rede;
            </li>
            <li>
              Suas preferências, com base no seu consumo dos nossos produtos e serviços, na sua
              navegação nas páginas da Contato, nos modelos dos dispositivos que você usa e, caso
              você autorize em cada aplicativo (como Netflix, WhatsApp, Amazon Prime Video e
              Facebook), para marketing direcionado — sem anúncios baseados em dados pessoais
              sensíveis;
            </li>
            <li>
              Cookies, usados para coletar informações que auxiliam a personalizar serviços,
              autenticar e facilitar sua navegação;
            </li>
            <li>Endereço IP do dispositivo utilizado para acessar os serviços ou produtos da Contato;</li>
            <li>Interações realizadas e perfil de uso do site e blog da Contato;</li>
            <li>Dados técnicos, como informações de URL, de conexão de rede, do provedor e do dispositivo;</li>
            <li>Atributos do dispositivo, tais como PPPoE, ID, sistema operacional, navegador e modelo;</li>
            <li>
              Dados de geolocalização do dispositivo, caso você autorize, para facilitar serviços
              de instalação e suporte;
            </li>
            <li>
              Dados de tráfego: volume de dados utilizados e antenas que atendem você, para
              mensurar a qualidade dos nossos serviços e para seu próprio controle.
            </li>
          </ul>

          <p className="font-semibold text-brand-deep">
            Dados pessoais originados do uso dos nossos produtos e serviços
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Dados de contratação de nossos produtos e serviços, tais como pacote de dados de internet;</li>
            <li>Histórico de crédito interno, tais como débitos em aberto na Contato;</li>
            <li>Histórico de atendimento ao cliente.</li>
          </ul>

          <p className="font-semibold text-brand-deep">Dados públicos</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Informações sobre você disponíveis publicamente ou que foram tornadas públicas por
              você;
            </li>
            <li>Informações sobre menções ou interações com a Contato;</li>
            <li>
              Depoimentos referentes à Contato postados em perfis e páginas nas redes sociais,
              juntamente com seu nome e imagem (incluindo fotos de perfil).
            </li>
          </ul>
        </Section>

        <Section title="3. Como a Contato usa seus dados pessoais">
          <p>
            A Contato utiliza os seus dados pessoais para poder prestar um serviço de alta
            qualidade e oferecer os melhores produtos a você. Detalhamos a seguir as finalidades
            para as quais utilizamos os seus dados pessoais:
          </p>

          <p className="font-semibold text-brand-deep">Dados pessoais informados pelo titular</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Prestação dos serviços e oferecimento dos produtos contratados;</li>
            <li>
              Identificação, autenticação e verificação de requisitos para contratação dos
              serviços e produtos da Contato;
            </li>
            <li>Atendimento de solicitações e dúvidas;</li>
            <li>
              Contato por telefone, e-mail, SMS, WhatsApp ou outros meios de comunicação, inclusive
              para envio de notificações de uso dos serviços da Contato (podendo incluir
              publicidade sobre upgrade no plano de internet contratado);
            </li>
            <li>
              Envio de prêmio (um presente, uma carta ou um mimo enviado quando o cliente é
              sorteado em campanhas publicitárias que incentivam o pagamento em dia);
            </li>
            <li>
              Aprimoramento dos serviços prestados, inclusive com o cruzamento de informações
              sobre produtos contratados por pessoa física e por pessoa jurídica, para oferecimento
              de novos produtos e serviços;
            </li>
            <li>
              Marketing, prospecção, pesquisas de mercado e de opinião, e promoção dos nossos
              produtos e serviços ou de nossos parceiros;
            </li>
            <li>
              Consultas sobre suas informações na base de dados do Sistema de Informações de
              Crédito (SCR), mediante a obtenção do seu consentimento;
            </li>
            <li>
              Reavaliações periódicas sobre a elegibilidade para ser um cliente da Contato,
              possibilitando o eventual convite proativo a prospectos;
            </li>
            <li>
              Proteção ao crédito, incluindo concessão de crédito para contratação de serviços,
              tais como plano de internet banda larga;
            </li>
            <li>Prevenção e resolução de problemas técnicos ou de segurança;</li>
            <li>
              Investigações e medidas de prevenção e combate a ilícitos, fraudes, crimes e garantia
              da segurança dos clientes da Contato;
            </li>
            <li>
              Exercício regular de direitos da Contato, inclusive apresentando documentos em
              processos judiciais e administrativos, se necessário;
            </li>
            <li>Colaboração ou cumprimento de ordem judicial, de autoridade competente ou de órgão fiscalizador;</li>
            <li>Cumprimento de obrigação legal ou regulatória.</li>
          </ul>

          <p className="font-semibold text-brand-deep">Dados biométricos</p>
          <p>
            Finalidade: prevenção à fraude e garantia da sua segurança nos processos de
            identificação e autenticação de cadastro.
          </p>

          <p className="font-semibold text-brand-deep">Dados pessoais que coletamos de terceiros</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Melhoria de nossos produtos e serviços;</li>
            <li>Marketing, prospecção, pesquisas de mercado e de opinião;</li>
            <li>
              Reavaliações periódicas sobre a elegibilidade para ser um cliente da Contato,
              possibilitando o eventual convite proativo a prospectos;
            </li>
            <li>
              Proteção ao crédito, incluindo concessão de crédito para contratação de serviços;
            </li>
            <li>Prevenção e resolução de problemas técnicos ou de segurança;</li>
            <li>
              Investigações e medidas de prevenção e combate a ilícitos, fraudes, crimes e garantia
              da segurança dos clientes da Contato;
            </li>
            <li>
              Cumprimento de obrigação legal ou regulatória, por exemplo, para manutenção do seu
              cadastro atualizado.
            </li>
          </ul>

          <p className="font-semibold text-brand-deep">Dados de navegação e do dispositivo</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Prestação dos serviços, oferecimento dos produtos contratados e aperfeiçoamento do
              uso e experiência com o site e blog da Contato;
            </li>
            <li>Operacionalização de novos produtos e serviços;</li>
            <li>
              Recomendação de novos serviços, produtos ou funcionalidades, inclusive de parceiros
              que possam interessar a você;
            </li>
            <li>Exibição de publicidade, seja no nosso site, redes sociais ou em sites de terceiros;</li>
            <li>
              Geração de estatísticas, estudos, pesquisas e levantamentos pertinentes às atividades
              e comportamento no uso dos produtos ou serviços;
            </li>
            <li>Proteção ao crédito, incluindo concessão de crédito para contratação de serviços;</li>
            <li>Prevenção e resolução de problemas técnicos ou de segurança;</li>
            <li>
              Dados de geolocalização para sua segurança — por exemplo, identificando compras
              indevidas;
            </li>
            <li>
              Investigações e medidas de prevenção e combate a ilícitos, fraudes, crimes e garantia
              da segurança dos clientes da Contato;
            </li>
            <li>
              Exercício regular de direitos da Contato, inclusive apresentando documentos em
              processos judiciais e administrativos, se necessário;
            </li>
            <li>Cumprimento de ordem judicial, de autoridade competente ou de órgão fiscalizador;</li>
            <li>Cumprimento de obrigação legal ou regulatória.</li>
          </ul>

          <p className="font-semibold text-brand-deep">
            Dados pessoais originados do uso dos nossos produtos e serviços
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Prestação dos serviços e oferecimento dos produtos contratados;</li>
            <li>Autenticação de transações, atendimento e suporte ao cliente;</li>
            <li>
              Aprimoramento dos serviços prestados, inclusive com o cruzamento de informações sobre
              produtos contratados por pessoa física e por pessoa jurídica;
            </li>
            <li>
              Desenvolvimento de novos produtos e serviços e geração de conhecimento para inovação;
            </li>
            <li>Testes para aprimoramento dos modelos, serviços e produtos da Contato;</li>
            <li>
              Marketing, prospecção, pesquisas de mercado e de opinião, e promoção dos nossos
              produtos e serviços ou de nossos parceiros;
            </li>
            <li>
              Envio de prêmio (um presente, uma carta ou um mimo enviado quando o cliente é
              sorteado em campanhas publicitárias que incentivam o pagamento em dia);
            </li>
            <li>Proteção ao crédito, incluindo concessão de crédito para contratação de serviços;</li>
            <li>
              Prevenção e resolução de problemas técnicos ou de segurança, e monitoramento de uso e
              performance dos serviços e produtos da Contato;
            </li>
            <li>
              Investigações e medidas de prevenção e combate a ilícitos, fraudes, crimes e garantia
              da segurança dos clientes da Contato;
            </li>
            <li>
              Exercício regular de direitos da Contato, inclusive apresentando documentos em
              processos judiciais e administrativos, se necessário;
            </li>
            <li>Colaboração ou cumprimento de ordem judicial, de autoridade competente ou de órgão fiscalizador;</li>
            <li>Cumprimento de obrigação legal ou regulatória.</li>
          </ul>

          <p className="font-semibold text-brand-deep">Dados públicos</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Divulgação dos produtos e serviços prestados pela Contato em redes sociais,
              websites, aplicativos ou materiais institucionais e publicitários;
            </li>
            <li>
              Envio de prêmio (um presente, uma carta ou um mimo enviado quando o cliente é
              sorteado em campanhas publicitárias que incentivam o pagamento em dia);
            </li>
            <li>Proteção ao crédito, incluindo concessão de crédito para contratação de serviços;</li>
            <li>
              Investigações e medidas de prevenção e combate a ilícitos, fraudes, crimes e garantia
              da segurança dos clientes da Contato;
            </li>
            <li>Exercício regular de direitos da Contato;</li>
            <li>Cumprimento de obrigações legais e/ou regulatórias impostas à Contato.</li>
          </ul>
        </Section>

        <Section title="4. Compartilhamento de dados pessoais">
          <p>
            A Contato poderá compartilhar os seus dados caso você solicite. O compartilhamento dos
            seus dados também poderá ser feito com terceiros parceiros e com autoridades e órgãos
            reguladores para diferentes finalidades, quando necessário.
          </p>
          <p>
            Sempre que efetuado, o compartilhamento de dados será realizado dentro dos limites e
            propósitos dos nossos negócios e de acordo com o que autoriza a legislação aplicável. A
            seguir, um resumo dos tipos de fornecedores com quem normalmente compartilhamos seus
            dados pessoais:
          </p>

          <p className="font-semibold text-brand-deep">
            4.1. Parceiros de negócios, prestadores de serviço e outros terceiros
          </p>
          <p>
            A Contato pode compartilhar seus dados com fornecedores de serviços de tecnologia da
            informação, de atendimento ao consumidor, de comunicação, de serviços estatísticos, de
            pesquisas, marketing, serviços financeiros e de meios de pagamentos; agências de
            cobrança, de crédito e prevenção a fraudes, bancos, instituições financeiras e outros
            terceiros, para:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Aprimoramento dos nossos serviços, site e operacionalização de novos produtos ou serviços;</li>
            <li>Auxílio na prestação dos serviços que entregamos a você, por exemplo, confecção e entrega do seu cartão;</li>
            <li>Contato por telefone, e-mail, SMS, WhatsApp, notificação ou outros meios de comunicação;</li>
            <li>Auxílio no desenvolvimento e oferta dos nossos produtos;</li>
            <li>Cobrança de dívidas;</li>
            <li>
              Checagem da sua identidade e elegibilidade para contratar os serviços da Contato, e
              reavaliações periódicas;
            </li>
            <li>Marketing, prospecção, pesquisas de mercado e de opinião;</li>
            <li>Proteção ao crédito, incluindo concessão de crédito para contratação de serviços;</li>
            <li>Prevenção e resolução de problemas técnicos ou de segurança;</li>
            <li>
              Investigações e medidas de prevenção e combate a ilícitos, fraudes, crimes e garantia
              da segurança dos clientes da Contato;
            </li>
            <li>Exercício regular de direitos da Contato;</li>
            <li>Cumprimento de ordem judicial, de autoridade competente ou de órgão fiscalizador;</li>
            <li>Cumprimento de obrigação legal ou regulatória.</li>
          </ul>

          <p className="font-semibold text-brand-deep">4.2. Autoridades e órgãos reguladores</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Investigações e medidas de prevenção e combate a ilícitos, fraudes, crimes e garantia
              da segurança dos clientes da Contato;
            </li>
            <li>
              Exercício regular de direitos da Contato, inclusive apresentando documentos em
              processos judiciais e administrativos, se necessário;
            </li>
            <li>Cumprimento de ordem judicial, atendimento de solicitação de autoridade competente ou órgão fiscalizador;</li>
            <li>Cumprimento de obrigação legal ou regulatória.</li>
          </ul>

          <p className="font-semibold text-brand-deep">4.3. Mediante sua solicitação</p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Garantir a transparência na nossa relação com você;</li>
            <li>Envio de notificações não obrigatórias por e-mail, WhatsApp e SMS.</li>
          </ul>

          <p>
            Além disso, ao utilizar nossos serviços ou navegar em nossos sites, você pode ser
            redirecionado para sites ou aplicativos de terceiros, como no caso do WhatsApp. Depois
            que você for redirecionado, as práticas de privacidade serão regidas pelas políticas
            desses terceiros. Não podemos controlar ou nos responsabilizar pelas práticas e
            conteúdo de privacidade de terceiros. Leia atentamente as políticas de privacidade
            aplicáveis para entender como eles coletam e processam seus dados.
          </p>
        </Section>

        <Section title="5. Retenção e exclusão dos seus dados pessoais">
          <p>
            Enquanto você for um Cliente ou Prospecto da Contato, durante o uso dos nossos
            produtos e serviços e por todo o período em que a Contato armazenar seus dados
            pessoais, eles serão mantidos em ambiente seguro e controlado.
          </p>
          <p>
            Quando aplicável, e mesmo após o cancelamento do serviço, podemos armazenar os seus
            dados pessoais por um período adicional para fins de auditoria, cumprimento de
            obrigações legais ou regulatórias, para o exercício regular de direitos da Contato ou
            pelo prazo necessário de acordo com a base legal que justifique a retenção. Por
            exemplo, podemos armazenar seus dados para cumprir com obrigações impostas pela
            Agência Nacional de Telecomunicações (Anatel) e para preservar nossos direitos em ações
            judiciais.
          </p>
        </Section>

        <Section title="6. Seus direitos como titular de dados pessoais">
          <p>
            A partir da entrada em vigor da LGPD, você, como titular de dados pessoais, pode
            exercer seus direitos frente aos controladores dos seus dados pessoais. Disponibilizamos
            os mecanismos detalhados abaixo para que você entenda de forma clara e transparente
            como exercer seus direitos, e nossa equipe estará pronta para atender eventuais
            solicitações:
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>
              <strong>Confirmação da existência de tratamento de dados pessoais:</strong> você pode
              solicitar à Contato que confirme se realiza o tratamento dos seus dados pessoais.
            </li>
            <li>
              <strong>Acesso aos dados pessoais:</strong> você pode solicitar que a Contato informe
              e forneça os dados pessoais que possui em relação a você.
            </li>
            <li>
              <strong>Correção de dados pessoais incompletos, inexatos ou desatualizados:</strong>{" "}
              você pode pedir a correção ou complementação. Caso não seja possível realizar a
              alteração por simples pedido em nossos canais, você precisará enviar um documento que
              comprove a forma correta e atual do dado.
            </li>
            <li>
              <strong>
                Anonimização, bloqueio ou eliminação de dados desnecessários, excessivos ou
                tratados em desconformidade com a LGPD:
              </strong>{" "}
              você pode solicitar essas medidas, desde que fique efetivamente constatado o
              excesso, a falta de necessidade ou a desconformidade com a lei.
            </li>
            <li>
              <strong>Eliminação dos dados pessoais tratados com o consentimento:</strong> caso
              você tenha dado o consentimento para tratamento dos seus dados para finalidades
              específicas (não necessárias à prestação dos nossos serviços), você poderá solicitar
              a eliminação desses dados.
            </li>
            <li>
              <strong>
                Informação das empresas com as quais a Contato compartilhou ou das quais recebeu
                seus dados pessoais:
              </strong>{" "}
              você pode solicitar essa informação.
            </li>
            <li>
              <strong>
                Informação sobre a possibilidade de não fornecer consentimento e sobre as
                consequências da negativa:
              </strong>{" "}
              caso seu consentimento seja necessário para acessar ou usar determinado produto ou
              serviço, você pode pedir esclarecimento sobre as consequências de não fornecê-lo.
            </li>
            <li>
              <strong>Revogação do consentimento:</strong> você pode solicitar a revogação a
              qualquer momento. Isso pode resultar na impossibilidade de uso de algumas
              funcionalidades, ou até no encerramento do serviço, mas não impede o uso de dados
              anonimizados ou de dados cujo tratamento esteja baseado em outra hipótese legal
              prevista na LGPD.
            </li>
            <li>
              <strong>Portabilidade e direito de petição:</strong> quando regulamentado pela
              Autoridade Nacional de Proteção de Dados (ANPD), você poderá solicitar a
              portabilidade dos seus dados a outro prestador de serviços, além de ter o direito de
              peticionar perante a autoridade nacional.
            </li>
          </ul>
          <p>
            Caso você deseje exercer qualquer um desses direitos, entre em contato conosco por
            meio dos canais de atendimento ou com o nosso encarregado (DPO). Para efetivarmos os
            seus direitos, podemos solicitar comprovação da sua identidade, como medida de
            segurança e prevenção à fraude.
          </p>
        </Section>

        <Section title="7. Registro de atividades">
          <p>
            Podemos registrar as atividades que você realiza quando utiliza nossos serviços, site
            ou blog, criando, quando possível e aplicável, logs que conterão: o endereço IP, acesso
            e ações realizadas por você no serviço disponibilizado (quando você fornecer seu
            consentimento para acesso remoto e suporte ao dispositivo), data e hora de cada ação e
            informações sobre o dispositivo utilizado, tais como versão de sistema operacional,
            navegador e geolocalização.
          </p>
          <p>
            Também podemos utilizar certas tecnologias, próprias ou de terceiros, de monitoramento
            das atividades realizadas enquanto você acessa nossos sites e blogs, tais como
            cookies, que são arquivos que armazenam de forma temporária o que você está visitando
            na rede. A Contato possui cookies em seus sites e blog que podem ser utilizados para
            diversos propósitos, incluindo lembrar-se de você e de suas preferências, persistir
            informações relacionadas a suas atividades no site visitado, ou coletar informações
            que podem ser usadas para oferecer conteúdo de forma personalizada. Nossos sites também
            podem utilizar objetos armazenados localmente para fornecer determinado conteúdo, tais
            como vídeo sob demanda, videoclipes ou animação.
          </p>
          <p>
            Você pode, a qualquer momento, bloquear o uso dos cookies ativando uma configuração no
            seu navegador de internet, e sua capacidade de limitar os cookies estará sujeita às
            configurações e limitações do seu navegador. Você também pode excluir os cookies
            existentes pelas mesmas configurações. Caso opte por desativar os cookies, você poderá
            continuar navegando nos sites e blogs, mas algumas partes das páginas poderão deixar de
            funcionar.
          </p>
          <p>
            Usamos também ferramentas de analytics, que podem coletar informações como a forma que
            você visita um site, incluindo quais páginas e quando você visita tais páginas, além de
            outros sites visitados antes, entre outras. Todas as tecnologias utilizadas por nós
            sempre respeitarão os termos desta Política de Privacidade.
          </p>
        </Section>

        <Section title="8. Transferência internacional de dados pessoais">
          <p>
            Alguns de seus dados pessoais, ou todos eles, poderão ser transferidos para o exterior,
            por exemplo quando armazenados pela Contato em servidores de computação em nuvem
            localizados fora do Brasil. Para isso, a Contato observa todos os requerimentos
            estabelecidos pela legislação vigente e adota as melhores práticas de segurança e
            privacidade para garantir a integridade e confidencialidade dos seus dados pessoais.
          </p>
        </Section>

        <Section title="9. Medidas de segurança">
          <p>
            A Contato usa diversos tipos de medidas de segurança para garantir a integridade de
            seus dados pessoais, como padrões de segurança de informação praticados pelo mercado
            quando coleta e armazena dados pessoais.
          </p>
          <p>
            Os dados pessoais podem, ainda, ser armazenados por meio de tecnologia de cloud
            computing e outras tecnologias que surjam futuramente, visando sempre a melhoria e o
            aperfeiçoamento de nossos serviços e segurança.
          </p>
          <p>
            Tratamos a segurança dos seus dados pessoais com o máximo de cuidado, utilizando
            padrões e melhores práticas adotados no mercado. Temos uma equipe responsável por
            garantir que a Contato adote as melhores práticas de segurança, dentre elas:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Segurança como código, a fim de viabilizar automações e respostas rápidas e
              eficientes para eventos de segurança no ambiente tecnológico;
            </li>
            <li>
              Criptografia para dados em repouso, em trânsito e em uso, para garantir a integridade
              das informações;
            </li>
            <li>Monitoramento contínuo do ambiente;</li>
            <li>Análises e testes contínuos de segurança da informação em nossos sistemas, feitos por times internos e externos;</li>
            <li>Auditorias periódicas.</li>
          </ul>
          <p>
            Recomendamos ainda que você nunca compartilhe sua senha com ninguém, ela é pessoal e
            intransferível. Sempre tome cuidado ao postar seus dados pessoais em redes sociais ou
            qualquer outro ambiente público. Para saber mais sobre segurança digital, acesse{" "}
            <a href="/blog" className="text-primary hover:underline">
              nosso blog
            </a>
            .
          </p>
        </Section>

        <Section title="10. Consentimento">
          <p>
            Ao longo desta Política de Privacidade, informamos que alguns dados pessoais somente
            serão coletados pela Contato com o seu consentimento. Da mesma forma, esses dados
            pessoais somente poderão ser tratados mediante autorização e para as finalidades
            descritas. Essas informações estão, principalmente, nos capítulos 2 e 3 desta Política.
          </p>
          <p>
            Ao ler esta Política de Privacidade e concordar com seus termos, você consente com o
            tratamento dos dados pessoais nas formas aqui indicadas.
          </p>
          <p>
            Vale lembrar que o tratamento de seus dados pessoais é condição necessária para que
            possamos prestar nossos serviços e entregar nossos produtos para você. Caso você tenha
            dúvidas sobre qualquer um dos termos explicados aqui, estamos à disposição em nossos
            canais de atendimento para ajudá-lo.
          </p>
        </Section>

        <Section title="11. Alterações a esta Política de Privacidade">
          <p>
            A Contato poderá alterar esta Política de Privacidade a qualquer tempo. Toda vez que
            alguma condição relevante desta Política for alterada, essas alterações serão válidas,
            eficazes e vinculantes após a nova versão ser divulgada no nosso site ou enviada por
            e-mail para você.
          </p>
          <p>
            Prezamos sempre pela transparência: toda vez que uma alteração relevante for feita, nós
            enviaremos um comunicado a você indicando a nova versão vigente. Ao continuar a usar
            nossos produtos e serviços após uma alteração, você estará concordando com as novas
            condições — mas você sempre pode manifestar a sua discordância pelos nossos canais de
            atendimento, se for o caso.
          </p>
          <p>
            Nas hipóteses em que as alterações a esta Política resultarem em mudanças nas práticas
            de tratamento de dados pessoais que dependam do seu consentimento, solicitaremos o seu
            consentimento com os novos termos em relação ao tratamento de dados e finalidades
            indicados.
          </p>
        </Section>

        <Section title="12. Fale conosco">
          <p>
            Segundo a Lei nº 13.709/2018, a Contato é considerada a "Controladora" dos seus dados
            pessoais. Se após a leitura desta Política de Privacidade você ainda tiver qualquer
            dúvida, ou por qualquer razão precisar se comunicar conosco para assuntos envolvendo os
            seus dados pessoais, você pode entrar em contato pelos canais abaixo:
          </p>
          <ul className="list-disc space-y-1 pl-5">
            <li>Telefones: (48) 3521-0400, (48) 3631-0600 e 0800 647 1592;</li>
            <li>WhatsApp: +55 48 3521-0400;</li>
            <li>
              Chat online:{" "}
              <a href="https://www.contato.net/" className="text-primary hover:underline">
                www.contato.net
              </a>
              ;
            </li>
            <li>
              E-mail do DPO:{" "}
              <a
                href="mailto:contato@contatointernet.com.br"
                className="text-primary hover:underline"
              >
                contato@contatointernet.com.br
              </a>
              ;
            </li>
            <li>
              Facebook:{" "}
              <a
                href="https://www.facebook.com/ContatoInternet/"
                target="_blank"
                rel="noreferrer"
                className="text-primary hover:underline"
              >
                facebook.com/ContatoInternet
              </a>
              .
            </li>
          </ul>
          <p>
            Estamos sempre à disposição para esclarecer suas dúvidas e colocar você no controle dos
            seus dados pessoais.
          </p>
        </Section>

        <hr className="border-border" />

        <h1 className="text-2xl font-extrabold uppercase tracking-tight text-primary sm:text-3xl">
          Política de Cookies
        </h1>
        <p>
          Este site usa cookies para melhorar a sua experiência enquanto navega pelo site. Os
          cookies que são categorizados como necessários são armazenados no seu navegador, pois
          são essenciais para o funcionamento das funcionalidades básicas do site.
        </p>
        <p>
          Também usamos cookies de terceiros que nos ajudam a analisar e entender como você usa
          este site. Estes cookies serão armazenados em seu navegador apenas com o seu
          consentimento. Você também tem a opção de recusá-los, mas a desativação de alguns desses
          cookies pode afetar sua experiência de navegação.
        </p>

        <Section title="Necessários">
          <p>
            Absolutamente essenciais para o funcionamento adequado do site. Esses cookies garantem
            funcionalidades básicas (como acessar recursos exclusivos por meio de login e senha) e
            recursos de segurança do site (como certificados de segurança que evitam fraudes).
          </p>
        </Section>

        <Section title="Funcionais">
          <p>
            Ajudam a realizar certas funcionalidades, como compartilhar o conteúdo do site em
            plataformas de mídia social, coletar feedbacks e outros recursos de terceiros.
          </p>
        </Section>

        <Section title="Performance">
          <p>
            Usados para compreender e analisar os principais índices de desempenho do site, o que
            ajuda a fornecer uma melhor experiência do usuário para os visitantes.
          </p>
        </Section>

        <Section title="Analíticos">
          <p>
            Usados para entender como os visitantes interagem com o site. Esses cookies ajudam a
            fornecer informações sobre métricas como número de visitantes, taxa de rejeição, origem
            do tráfego, etc. Para isso, utilizamos o Google Analytics, com o qual compartilhamos os
            dados gerados em nosso site para entender como melhorar a sua experiência conosco.
          </p>
        </Section>

        <Section title="Marketing">
          <p>
            Usados para fornecer aos visitantes anúncios e campanhas de marketing relevantes. Esses
            cookies rastreiam visitantes em sites e coletam informações para fornecer anúncios
            personalizados.
          </p>
        </Section>

        <Section title="Outros">
          <p>
            Cookies não categorizados são aqueles que estão sendo analisados e ainda não foram
            classificados em uma categoria.
          </p>
        </Section>
      </div>
    </div>
  );
}
